(() => {
  let toastTimer;
  const sideCarts = new Set();
  const shopRoot = window.Shopify?.routes?.root || '/';

  const cartEndpoint = (path) => `${shopRoot}${path}`;

  const announce = (message) => {
    const toast = document.querySelector('[data-feax-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.hidden = false;
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => { toast.hidden = true; }, 3500);
  };

  const updateCartCounts = (itemCount) => {
    document.querySelectorAll('[data-feax-cart-count]').forEach((count) => {
      count.textContent = itemCount;
      count.hidden = itemCount === 0;
    });
    document.querySelectorAll('[data-feax-cart-toggle]').forEach((toggle) => {
      toggle.setAttribute('aria-label', `Carrito, ${itemCount} productos`);
    });
  };

  const activeSideCarts = () => {
    const active = [...sideCarts].filter((controller) => controller.isConnected());
    sideCarts.forEach((controller) => {
      if (!controller.isConnected()) sideCarts.delete(controller);
    });
    return active;
  };

  const initMenu = (root) => {
    const header = root.matches?.('[data-feax-header]') ? root : root.querySelector('[data-feax-header]');
    if (!header) return;

    const menuButton = header.querySelector('[data-feax-menu-toggle]');
    const menu = header.querySelector('[data-feax-nav]');
    const searchButton = header.querySelector('[data-feax-search-toggle]');
    const search = header.querySelector('[data-feax-search]');
    if (menuButton && menu && !menuButton.dataset.feaxInitialized) {
      menuButton.dataset.feaxInitialized = 'true';
      menuButton.addEventListener('click', () => {
        const willOpen = menuButton.getAttribute('aria-expanded') !== 'true';
        menuButton.setAttribute('aria-expanded', String(willOpen));
        menu.classList.toggle('is-open', willOpen);
        if (willOpen && searchButton && search) {
          searchButton.setAttribute('aria-expanded', 'false');
          search.classList.remove('is-open');
        }
        if (!willOpen) {
          menu.querySelectorAll('.is-submenu-open').forEach((item) => item.classList.remove('is-submenu-open'));
          menu.querySelectorAll('[data-feax-submenu-toggle]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
        }
      });
    }

    if (searchButton && search && !searchButton.dataset.feaxInitialized) {
      searchButton.dataset.feaxInitialized = 'true';
      const closeSearch = () => {
        searchButton.setAttribute('aria-expanded', 'false');
        search.classList.remove('is-open');
      };

      searchButton.addEventListener('click', () => {
        const willOpen = searchButton.getAttribute('aria-expanded') !== 'true';
        searchButton.setAttribute('aria-expanded', String(willOpen));
        search.classList.toggle('is-open', willOpen);
        if (willOpen) {
          menuButton?.setAttribute('aria-expanded', 'false');
          menu?.classList.remove('is-open');
          window.requestAnimationFrame(() => search.querySelector('input[type="search"]')?.focus());
        }
      });

      document.addEventListener('pointerdown', (event) => {
        if (!header.isConnected || searchButton.getAttribute('aria-expanded') !== 'true') return;
        if (search.contains(event.target) || searchButton.contains(event.target)) return;
        closeSearch();
      });

      document.addEventListener('keydown', (event) => {
        if (!header.isConnected || event.key !== 'Escape' || searchButton.getAttribute('aria-expanded') !== 'true') return;
        closeSearch();
        searchButton.focus();
      });
    }

    header.querySelectorAll('[data-feax-submenu-toggle]').forEach((button) => {
      if (button.dataset.feaxInitialized) return;
      button.dataset.feaxInitialized = 'true';
      button.addEventListener('click', () => {
        const item = button.closest('[data-feax-nav-item]');
        if (!item) return;
        const willOpen = button.getAttribute('aria-expanded') !== 'true';
        const siblings = item.parentElement?.children || [];
        [...siblings].forEach((sibling) => {
          if (sibling === item) return;
          sibling.classList?.remove('is-submenu-open');
          sibling.querySelector?.(':scope > .feax-nav__link-row [data-feax-submenu-toggle]')?.setAttribute('aria-expanded', 'false');
        });
        item.classList.toggle('is-submenu-open', willOpen);
        button.setAttribute('aria-expanded', String(willOpen));
      });
    });
  };

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'es-PA', {
        style: 'currency',
        currency: currency || window.Shopify?.currency?.active || 'USD'
      }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  };

  const makeElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };

  const initSideCart = (root) => {
    const header = root.matches?.('[data-feax-header]') ? root : root.querySelector('[data-feax-header]');
    const drawer = header?.querySelector('[data-feax-side-cart]');
    const trigger = header?.querySelector('[data-feax-cart-toggle]');
    const overlay = header?.querySelector('[data-feax-cart-overlay]');
    if (!header || !drawer || !trigger || !overlay || drawer.dataset.feaxInitialized) return;

    drawer.dataset.feaxInitialized = 'true';
    const itemsRoot = drawer.querySelector('[data-feax-cart-items]');
    const emptyState = drawer.querySelector('[data-feax-cart-empty]');
    const footer = drawer.querySelector('[data-feax-cart-footer]');
    const subtotal = drawer.querySelector('[data-feax-cart-subtotal]');
    const status = drawer.querySelector('[data-feax-cart-status]');
    let previousFocus = null;
    let busy = false;

    const setBusy = (isBusy, message = '') => {
      busy = isBusy;
      drawer.classList.toggle('is-loading', isBusy);
      if (status) status.textContent = message;
    };

    const renderItem = (item, index, currency) => {
      const article = makeElement('article', 'feax-side-cart__item');

      const imageLink = makeElement('a', 'feax-side-cart__image');
      imageLink.href = item.url || '#';
      if (item.image) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = item.featured_image?.alt || item.product_title || '';
        image.width = 84;
        image.height = 84;
        image.loading = 'lazy';
        imageLink.append(image);
      }

      const details = makeElement('div', 'feax-side-cart__details');
      const title = makeElement('a', 'feax-side-cart__item-title', item.product_title);
      title.href = item.url || '#';
      details.append(title);

      if (item.variant_title && item.variant_title !== 'Default Title') {
        details.append(makeElement('p', 'feax-side-cart__variant', item.variant_title));
      }

      details.append(makeElement('strong', 'feax-side-cart__price', formatMoney(item.final_line_price, currency)));

      const controls = makeElement('div', 'feax-side-cart__controls');
      const decrease = makeElement('button', 'feax-side-cart__quantity-button', '−');
      decrease.type = 'button';
      decrease.dataset.feaxCartLine = String(index + 1);
      decrease.dataset.feaxCartQuantity = String(Math.max(0, item.quantity - 1));
      decrease.setAttribute('aria-label', `Reducir cantidad de ${item.product_title}`);

      const quantity = makeElement('span', 'feax-side-cart__quantity', String(item.quantity));
      quantity.setAttribute('aria-label', `Cantidad: ${item.quantity}`);

      const increase = makeElement('button', 'feax-side-cart__quantity-button', '+');
      increase.type = 'button';
      increase.dataset.feaxCartLine = String(index + 1);
      increase.dataset.feaxCartQuantity = String(item.quantity + 1);
      increase.setAttribute('aria-label', `Aumentar cantidad de ${item.product_title}`);

      const remove = makeElement('button', 'feax-side-cart__remove', 'Eliminar');
      remove.type = 'button';
      remove.dataset.feaxCartLine = String(index + 1);
      remove.dataset.feaxCartQuantity = '0';
      remove.setAttribute('aria-label', `Eliminar ${item.product_title} del carrito`);

      controls.append(decrease, quantity, increase, remove);
      details.append(controls);
      article.append(imageLink, details);
      return article;
    };

    const render = (cart) => {
      if (!cart || !itemsRoot) return;
      const currency = cart.currency || window.Shopify?.currency?.active || 'USD';
      itemsRoot.replaceChildren(...cart.items.map((item, index) => renderItem(item, index, currency)));
      updateCartCounts(cart.item_count);
      emptyState.hidden = cart.item_count !== 0;
      footer.hidden = cart.item_count === 0;
      if (subtotal) subtotal.textContent = formatMoney(cart.total_price, currency);
      setBusy(false, cart.item_count ? `${cart.item_count} productos en el carrito.` : 'El carrito está vacío.');
    };

    const refresh = async () => {
      setBusy(true, 'Cargando carrito…');
      try {
        const response = await fetch(cartEndpoint('cart.js'), { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('No se pudo cargar el carrito.');
        const cart = await response.json();
        render(cart);
        return cart;
      } catch (error) {
        setBusy(false, error.message || 'No se pudo cargar el carrito.');
        announce(error.message || 'No se pudo cargar el carrito.');
        return null;
      }
    };

    const open = (cart) => {
      previousFocus = document.activeElement;
      overlay.hidden = false;
      window.requestAnimationFrame(() => {
        overlay.classList.add('is-open');
        drawer.classList.add('is-open');
      });
      drawer.setAttribute('aria-hidden', 'false');
      trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('feax-side-cart-open');
      drawer.querySelector('[data-feax-cart-close]')?.focus();
      if (cart) render(cart); else refresh();
    };

    const close = () => {
      drawer.classList.remove('is-open');
      overlay.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      trigger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('feax-side-cart-open');
      window.setTimeout(() => { overlay.hidden = true; }, 250);
      if (previousFocus?.isConnected) previousFocus.focus();
    };

    const changeLine = async (line, quantity) => {
      if (busy) return;
      setBusy(true, 'Actualizando carrito…');
      try {
        const response = await fetch(cartEndpoint('cart/change.js'), {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity })
        });
        if (!response.ok) throw new Error('No se pudo actualizar el carrito.');
        render(await response.json());
      } catch (error) {
        setBusy(false, error.message || 'No se pudo actualizar el carrito.');
        announce(error.message || 'No se pudo actualizar el carrito.');
      }
    };

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      open();
    });
    overlay.addEventListener('click', close);
    drawer.querySelectorAll('[data-feax-cart-close]').forEach((button) => button.addEventListener('click', close));
    drawer.addEventListener('click', (event) => {
      const button = event.target.closest('[data-feax-cart-line]');
      if (!button) return;
      changeLine(Number(button.dataset.feaxCartLine), Number(button.dataset.feaxCartQuantity));
    });
    document.addEventListener('keydown', (event) => {
      if (!drawer.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    sideCarts.add({ open, render, refresh, isConnected: () => drawer.isConnected });
  };

  const initSteps = (root) => {
    const stepsRoot = root.querySelector('[data-feax-steps]');
    if (!stepsRoot || stepsRoot.dataset.feaxInitialized) return;

    stepsRoot.dataset.feaxInitialized = 'true';
    const steps = [...stepsRoot.querySelectorAll('[data-feax-step]')];
    const current = stepsRoot.querySelector('[data-feax-step-current]');
    if (!steps.length) return;
    let active = 0;

    const showStep = (index) => {
      active = (index + steps.length) % steps.length;
      const previousIndex = (active - 1 + steps.length) % steps.length;
      const nextIndex = (active + 1) % steps.length;
      steps.forEach((step, stepIndex) => {
        step.classList.remove('is-active', 'is-prev', 'is-next');
        if (stepIndex === active) step.classList.add('is-active');
        else if (steps.length === 2 && stepIndex === nextIndex) step.classList.add('is-next');
        else if (steps.length > 2 && stepIndex === previousIndex) step.classList.add('is-prev');
        else if (steps.length > 2 && stepIndex === nextIndex) step.classList.add('is-next');

        const inactive = stepIndex !== active;
        step.setAttribute('aria-hidden', String(inactive));
        if ('inert' in step) step.inert = inactive;
      });
      if (current) current.textContent = String(active + 1).padStart(2, '0');
    };

    stepsRoot.querySelector('[data-feax-step-prev]')?.addEventListener('click', () => showStep(active - 1));
    stepsRoot.querySelector('[data-feax-step-next]')?.addEventListener('click', () => showStep(active + 1));
    stepsRoot.addEventListener('shopify:block:select', (event) => {
      const selectedStep = event.target.closest?.('[data-feax-step]');
      const selectedIndex = steps.indexOf(selectedStep);
      if (selectedIndex >= 0) showStep(selectedIndex);
    });
    showStep(0);
  };

  const initProductCarousels = (root) => {
    const carousels = [
      ...(root.matches?.('[data-feax-products-carousel]') ? [root] : []),
      ...root.querySelectorAll('[data-feax-products-carousel]')
    ];

    carousels.forEach((carousel) => {
      if (carousel.dataset.feaxInitialized) return;
      const viewport = carousel.querySelector('[data-feax-products-viewport]');
      const track = carousel.querySelector('[data-feax-products-track]');
      const cards = [...(track?.querySelectorAll('.feax-product-card') || [])];
      const controls = carousel.querySelector('[data-feax-products-controls]');
      const previous = carousel.querySelector('[data-feax-products-prev]');
      const next = carousel.querySelector('[data-feax-products-next]');
      const dotsRoot = carousel.querySelector('[data-feax-products-dots]');
      if (!viewport || !track || !cards.length) return;

      carousel.dataset.feaxInitialized = 'true';
      let page = 0;
      let pageCount = 1;
      let visibleCards = 1;
      let cardWidth = 0;
      let gap = 0;
      let pointerStart = null;
      let didSwipe = false;

      const updateCardsAccessibility = () => {
        const firstVisible = page * visibleCards;
        const lastVisible = firstVisible + visibleCards;
        cards.forEach((card, index) => {
          const hidden = index < firstVisible || index >= lastVisible;
          card.setAttribute('aria-hidden', String(hidden));
          if ('inert' in card) card.inert = hidden;
        });
      };

      const updateDots = () => {
        if (!dotsRoot) return;
        [...dotsRoot.children].forEach((dot, index) => {
          const active = index === page;
          dot.classList.toggle('is-active', active);
          if (active) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      };

      const showPage = (newPage, animate = true) => {
        if (pageCount <= 1) newPage = 0;
        else newPage = (newPage + pageCount) % pageCount;
        page = newPage;
        track.style.transitionDuration = animate ? '' : '0ms';
        const offset = page * visibleCards * (cardWidth + gap);
        track.style.transform = `translate3d(${-offset}px, 0, 0)`;
        updateDots();
        updateCardsAccessibility();
      };

      const buildDots = () => {
        if (!dotsRoot) return;
        dotsRoot.replaceChildren();
        for (let index = 0; index < pageCount; index += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'feax-product-pagination__dot';
          dot.setAttribute('aria-label', `Ir para a página ${index + 1} de ${pageCount}`);
          dot.addEventListener('click', () => showPage(index));
          dotsRoot.append(dot);
        }
      };

      const recalculate = () => {
        const viewportWidth = viewport.getBoundingClientRect().width;
        if (!viewportWidth) return;

        const desktopColumns = Math.max(1, Number(carousel.dataset.columnsDesktop) || 4);
        const smallDesktopColumns = Math.max(1, Number(carousel.dataset.columnsSmallDesktop) || Math.min(3, desktopColumns));
        const tabletColumns = Math.max(1, Number(carousel.dataset.columnsTablet) || Math.min(2, desktopColumns));
        const mobileColumns = Math.max(1, Number(carousel.dataset.columnsMobile) || 1);
        let requestedCards = desktopColumns;
        if (window.innerWidth <= 600) requestedCards = mobileColumns;
        else if (window.innerWidth <= 900) requestedCards = tabletColumns;
        else if (window.innerWidth <= 1200) requestedCards = smallDesktopColumns;
        visibleCards = Math.max(1, Math.min(requestedCards, cards.length));
        gap = parseFloat(window.getComputedStyle(track).gap) || 0;
        cardWidth = (viewportWidth - gap * (visibleCards - 1)) / visibleCards;
        carousel.style.setProperty('--feax-carousel-card-width', `${cardWidth}px`);

        const nextPageCount = Math.ceil(cards.length / visibleCards);
        if (nextPageCount !== pageCount) {
          pageCount = nextPageCount;
          page = Math.min(page, pageCount - 1);
          buildDots();
        }
        if (controls) controls.hidden = pageCount <= 1;
        showPage(page, false);
        window.requestAnimationFrame(() => { track.style.transitionDuration = ''; });
      };

      previous?.addEventListener('click', () => showPage(page - 1));
      next?.addEventListener('click', () => showPage(page + 1));

      viewport.addEventListener('pointerdown', (event) => {
        if (event.button !== 0 || pageCount <= 1) return;
        pointerStart = event.clientX;
        didSwipe = false;
      }, { passive: true });
      viewport.addEventListener('pointerup', (event) => {
        if (pointerStart === null) return;
        const distance = event.clientX - pointerStart;
        pointerStart = null;
        if (Math.abs(distance) < 45) return;
        didSwipe = true;
        showPage(distance < 0 ? page + 1 : page - 1);
      }, { passive: true });
      viewport.addEventListener('pointercancel', () => { pointerStart = null; });
      viewport.addEventListener('click', (event) => {
        if (!didSwipe) return;
        event.preventDefault();
        event.stopPropagation();
        didSwipe = false;
      }, true);

      carousel.addEventListener('shopify:block:select', (event) => {
        const selectedCard = event.target.closest?.('.feax-product-card');
        const selectedIndex = cards.indexOf(selectedCard);
        if (selectedIndex >= 0) showPage(Math.floor(selectedIndex / visibleCards), false);
      });

      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(recalculate);
        observer.observe(viewport);
      } else {
        window.addEventListener('resize', recalculate, { passive: true });
      }

      recalculate();
    });
  };

  const initProductForms = (root) => {
    root.querySelectorAll('[data-feax-add-form]').forEach((form) => {
      if (form.dataset.feaxInitialized) return;
      form.dataset.feaxInitialized = 'true';

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = form.querySelector('button[type="submit"]');
        const original = button?.textContent;
        if (button) {
          button.disabled = true;
          button.textContent = 'Añadiendo…';
        }

        try {
          const response = await fetch(cartEndpoint('cart/add.js'), {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          if (!response.ok) throw new Error('No se pudo añadir el producto.');

          const cartResponse = await fetch(cartEndpoint('cart.js'), { headers: { Accept: 'application/json' } });
          if (!cartResponse.ok) throw new Error('Producto añadido, pero no se pudo cargar el carrito.');
          const cart = await cartResponse.json();
          const carts = activeSideCarts();
          carts.forEach((controller) => controller.render(cart));
          carts[0]?.open(cart);
          updateCartCounts(cart.item_count);
          announce('Producto añadido al carrito.');
        } catch (error) {
          announce(error.message || 'No se pudo añadir el producto.');
        } finally {
          if (button) {
            button.disabled = false;
            button.textContent = original;
          }
        }
      });
    });
  };

  const initProductDetails = (root) => {
    const details = [
      ...(root.matches?.('.feax-product-detail') ? [root] : []),
      ...root.querySelectorAll('.feax-product-detail')
    ];

    details.forEach((detail) => {
      if (detail.dataset.feaxProductInitialized) return;
      detail.dataset.feaxProductInitialized = 'true';

      detail.querySelectorAll('[data-feax-product-quantity]').forEach((quantity) => {
        const input = quantity.querySelector('input[name="quantity"]');
        if (!input) return;
        quantity.querySelectorAll('[data-feax-quantity-change]').forEach((button) => {
          button.addEventListener('click', () => {
            const minimum = Math.max(1, Number(input.min) || 1);
            const current = Math.max(minimum, Number(input.value) || minimum);
            input.value = Math.max(minimum, current + Number(button.dataset.feaxQuantityChange || 0));
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });
        input.addEventListener('change', () => {
          input.value = Math.max(1, Number(input.value) || 1);
        });
      });

      detail.querySelectorAll('[data-feax-variant-select]').forEach((select) => {
        select.addEventListener('change', () => {
          const option = select.selectedOptions[0];
          const price = detail.querySelector('[data-feax-product-price]');
          const submit = select.form?.querySelector('button[type="submit"]');
          const available = option?.dataset.available === 'true';
          if (price && option?.dataset.price) price.textContent = option.dataset.price;
          if (submit) submit.disabled = !available;
        });
      });
    });
  };

  const init = (root = document) => {
    initMenu(root);
    initSideCart(root);
    initSteps(root);
    initProductCarousels(root);
    initProductDetails(root);
    initProductForms(root);
  };

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
